/**
 * Time range picker — the Recordings page's "Time range" control as a component: a trigger
 * showing the APPLIED range, and a popover to pick start AND end together, confirmed with OK.
 *
 * Same behaviour the operators already know from Recordings:
 *   - two stage chips (From → To) say which end is being edited;
 *   - hour / minute / AM-PM columns — our own, because the native time picker cannot be closed
 *     programmatically on the AM/PM pick;
 *   - the AM/PM click COMMITS the stage: From advances to To, and To applies the range and closes;
 *   - OK applies, Cancel or a click outside reverts to the applied range.
 *
 * The panel is portaled to <body> and placed in viewport coordinates, flipping to whichever side
 * of the trigger has room — inside a short, overflow-hidden column (the Recorded wall's timeline
 * header) an in-flow popover would be clipped exactly when someone needs it.
 *
 * Values are "HH:mm", 24-hour, local time.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ClockIcon } from '@heroicons/react/24/outline';

export interface TimeRange { start: string; end: string }

export interface TimeRangePickerProps {
  value: TimeRange;
  onApply: (next: TimeRange) => void;
  className?: string;
}

const parseHM = (v: string) => {
  const [H, M] = (v || '00:00').split(':').map(Number);
  return { h12: (H % 12) || 12, min: M || 0, pm: H >= 12 };
};
const buildHM = (h12: number, min: number, pm: boolean) =>
  `${String((h12 % 12) + (pm ? 12 : 0)).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
export const fmt12 = (v: string) => {
  const { h12, min, pm } = parseHM(v);
  return `${h12}:${String(min).padStart(2, '0')} ${pm ? 'PM' : 'AM'}`;
};

const PANEL_W = 240;
const EDGE = 8;

export default function TimeRangePicker({ value, onApply, className = '' }: TimeRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<'from' | 'to'>('from');
  const [pending, setPending] = useState<TimeRange>(value);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // The applied range can change from outside (a new day fits the range to its footage).
  useEffect(() => { if (!open) setPending(value); }, [value, open]);

  const valid = pending.start < pending.end;

  const close = useCallback((apply: boolean) => {
    if (apply && pending.start < pending.end) onApply(pending);
    else setPending(value);
    setOpen(false);
  }, [pending, value, onApply]);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const H = panelRef.current?.offsetHeight || 270;
    const above = r.top - H - 6;
    const top = above >= EDGE ? above : Math.min(r.bottom + 6, window.innerHeight - H - EDGE);
    setPos({ top: Math.max(EDGE, top), left: Math.max(EDGE, Math.min(r.left, window.innerWidth - PANEL_W - EDGE)) });
  }, []);

  // Re-measure while open: a scroll or resize moves the trigger, and a fixed panel does not follow.
  useEffect(() => {
    if (!open) { setPos(null); return undefined; }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  // Click outside = Cancel. The panel is outside the trigger's DOM subtree, so it needs its own check.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      close(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const stageValue = stage === 'from' ? pending.start : pending.end;

  // Keep the selected hour/minute visible in their scroll columns.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelectorAll<HTMLElement>('[data-col]').forEach((col) => {
      const sel = col.querySelector<HTMLElement>('[data-selected="1"]');
      if (sel) col.scrollTop = sel.offsetTop - col.clientHeight / 2 + sel.clientHeight / 2;
    });
  }, [open, pos, stage, stageValue]);

  const setPart = (part: 'h12' | 'min' | 'pm', val: number | boolean) => {
    const cur = parseHM(stageValue);
    const next = { ...cur, [part]: val };
    const hm = buildHM(next.h12, next.min, next.pm);
    setPending((p) => (stage === 'from' ? { ...p, start: hm } : { ...p, end: hm }));
    return hm;
  };

  const pickAmPm = (pm: boolean) => {
    const hm = setPart('pm', pm);
    if (stage === 'from') { setStage('to'); return; }
    // Final commit — apply and close when valid, else stay open with the error showing.
    if (pending.start < hm) {
      onApply({ start: pending.start, end: hm });
      setOpen(false);
    }
  };

  const sv = parseHM(stageValue);
  const chip = (active: boolean) =>
    `flex-1 rounded px-2 py-1 font-mono transition-colors border ${
      active ? 'bg-brand-lighter text-brand border-brand' : 'border-border text-muted-foreground hover:bg-muted'
    }`;

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => { setStage('from'); setOpen((o) => !o); }}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Time range"
        className={`flex items-center gap-1.5 rounded border px-2 py-0.5 text-[11px] text-foreground transition-colors hover:bg-muted ${
          open ? 'border-brand ring-1 ring-brand-ring' : 'border-border'
        } ${className}`}
      >
        <ClockIcon className="h-3.5 w-3.5 flex-none text-muted-foreground" />
        <span className="truncate font-mono">{fmt12(value.start)} — {fmt12(value.end)}</span>
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Pick a time range"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: PANEL_W, zIndex: 9999 }}
          className="space-y-2.5 rounded-lg border border-border bg-card p-3 shadow-modal"
        >
          <div className="flex items-center gap-1.5 text-[11px]">
            <button onClick={() => setStage('from')} className={chip(stage === 'from')}>{fmt12(pending.start)}</button>
            <span className="text-muted-foreground">→</span>
            <button onClick={() => setStage('to')} className={chip(stage === 'to')}>{fmt12(pending.end)}</button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            {stage === 'from' ? 'Pick start time — AM/PM confirms' : 'Pick end time — AM/PM applies'}
          </p>
          <div className="flex gap-1.5">
            <div data-col className="h-36 flex-1 overflow-y-auto rounded border border-border" style={{ scrollbarWidth: 'thin' }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                <button
                  key={h}
                  data-selected={sv.h12 === h ? '1' : undefined}
                  onClick={() => setPart('h12', h)}
                  className={`block w-full py-1 text-center text-[12px] transition-colors ${sv.h12 === h ? 'bg-brand text-white' : 'text-foreground hover:bg-muted'}`}
                >
                  {h}
                </button>
              ))}
            </div>
            <div data-col className="h-36 flex-1 overflow-y-auto rounded border border-border" style={{ scrollbarWidth: 'thin' }}>
              {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                <button
                  key={m}
                  data-selected={sv.min === m ? '1' : undefined}
                  onClick={() => setPart('min', m)}
                  className={`block w-full py-1 text-center text-[12px] transition-colors ${sv.min === m ? 'bg-brand text-white' : 'text-foreground hover:bg-muted'}`}
                >
                  {String(m).padStart(2, '0')}
                </button>
              ))}
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <button
                onClick={() => pickAmPm(false)}
                className={`flex-1 rounded border text-[12px] font-medium transition-colors ${!sv.pm ? 'border-brand bg-brand text-white' : 'border-border text-foreground hover:bg-muted'}`}
              >
                AM
              </button>
              <button
                onClick={() => pickAmPm(true)}
                className={`flex-1 rounded border text-[12px] font-medium transition-colors ${sv.pm ? 'border-brand bg-brand text-white' : 'border-border text-foreground hover:bg-muted'}`}
              >
                PM
              </button>
            </div>
          </div>
          {!valid && <p className="text-[10px] text-red-500">Start must be before end</p>}
          <div className="flex justify-end gap-2 pt-0.5">
            <button
              onClick={() => close(false)}
              className="rounded border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={() => close(true)}
              disabled={!valid}
              className="rounded bg-brand px-3 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-40"
            >
              OK
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
